// src/components/admin/SessionManager.tsx
// Sistema de gestión de sesión con advertencia de expiración
import React, { useEffect, useState, useCallback, useRef } from 'react';
import './SessionManager.css';

interface SessionManagerProps {
  onSessionExpired: () => void;
  sessionTimeout?: number; // Tiempo total de sesión en ms (default: 30 min)
  warningTime?: number; // Tiempo de advertencia antes de expirar en ms (default: 30 seg)
}

const SessionManager: React.FC<SessionManagerProps> = ({
  onSessionExpired,
  sessionTimeout = 30 * 60 * 1000, // 30 minutos
  warningTime = 30 * 1000, // 30 segundos
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Limpiar todos los timers
  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  // Iniciar countdown
  const startCountdown = useCallback(() => {
    setCountdown(30);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearAllTimers();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearAllTimers]);

  // Resetear timers de sesión
  const resetSessionTimers = useCallback(() => {
    console.log('🔄 Reseteando timers de sesión');
    clearAllTimers();
    setShowWarning(false);
    lastActivityRef.current = Date.now();

    // Timer para mostrar advertencia
    warningTimeoutRef.current = setTimeout(() => {
      console.log('⚠️ Mostrando advertencia de sesión');
      setShowWarning(true);
      startCountdown();
    }, sessionTimeout - warningTime);

    // Timer para cerrar sesión
    timeoutRef.current = setTimeout(() => {
      console.log('🚪 Sesión expirada por inactividad');
      clearAllTimers();
      onSessionExpired();
    }, sessionTimeout);
  }, [sessionTimeout, warningTime, clearAllTimers, startCountdown, onSessionExpired]);

  // Eventos de actividad del usuario
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Solo resetear si han pasado más de 5 segundos (evitar spam)
    if (timeSinceLastActivity > 5000) {
      resetSessionTimers();
    }
  }, [resetSessionTimers]);

  // Continuar sesión (desde el modal)
  const handleContinueSession = useCallback(() => {
    console.log('✅ Usuario decidió continuar sesión');
    resetSessionTimers();
  }, [resetSessionTimers]);

  // Cerrar sesión inmediatamente
  const handleLogoutNow = useCallback(() => {
    console.log('🚪 Usuario decidió cerrar sesión');
    clearAllTimers();
    onSessionExpired();
  }, [clearAllTimers, onSessionExpired]);

  // Configurar listeners de actividad
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    // Iniciar timers al montar
    resetSessionTimers();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      clearAllTimers();
    };
  }, [handleUserActivity, resetSessionTimers, clearAllTimers]);

  // Interceptar errores 401 en fetch
  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      if (response.status === 401) {
        console.log('❌ Error 401 detectado - Token expirado');
        clearAllTimers();
        onSessionExpired();
      }
      
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [clearAllTimers, onSessionExpired]);

  if (!showWarning) return null;

  return (
    <div className="session-warning-overlay">
      <div className="session-warning-modal">
        <div className="session-warning-header">
          <div className="warning-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h3>Sesión por Expirar</h3>
        </div>

        <div className="session-warning-body">
          <p>
            Tu sesión va a expirar por inactividad en:
          </p>
          
          <div className="countdown-display">
            <div className="countdown-number">{countdown}</div>
            <div className="countdown-label">segundos</div>
          </div>

          <p className="warning-message">
            <i className="fas fa-info-circle"></i>
            ¿Deseas continuar con tu sesión?
          </p>
        </div>

        <div className="session-warning-footer">
          <button 
            className="btn btn-secondary"
            onClick={handleLogoutNow}
          >
            <i className="fas fa-sign-out-alt"></i>
            Cerrar Sesión
          </button>
          
          <button 
            className="btn btn-primary"
            onClick={handleContinueSession}
          >
            <i className="fas fa-check"></i>
            Continuar Sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;
